import React from 'react'
import * as api from '../lib/api'

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

const formatMoney = (amount) => {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(amount)
}

const firstDayOfMonth = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

const lastDayOfMonth = () => {
  const d = new Date()
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(last).padStart(2, '0')}`
}

export default function AutoSalaryBreakdown({ autoSalary, filterEmployee, profile, filterPeriodStart, filterPeriodEnd, setSalaryDetailModal, isAdmin, isOwner, onPaySalary }) {
  const [isPaying, setIsPaying] = React.useState(false)
  if (!autoSalary) return null

  const dailyDetailsArr = autoSalary.dailyDetails ? Object.values(autoSalary.dailyDetails) : []
  const fullShifts = dailyDetailsArr.filter(d => (parseFloat(d.hours) || 0) >= 12).length
  const partialShifts = dailyDetailsArr.filter(d => (parseFloat(d.hours) || 0) > 0 && (parseFloat(d.hours) || 0) < 12).length

  const hasOpsDetails = dailyDetailsArr.some(d => d.opsAccepted !== undefined)

  let acceptedNoHelper = 0
  let acceptedLateNoHelper = 0
  let acceptBonusNoHelper = 0

  let acceptedWithHelper = 0
  let acceptedLateWithHelper = 0
  let acceptBonusWithHelper = 0

  let issuedNoHelper = 0
  let issuedBonusNoHelper = 0

  let issuedWithHelper = 0
  let issuedBonusWithHelper = 0

  let returnedNoHelper = 0
  let returnBonusNoHelper = 0

  let returnedWithHelper = 0
  let returnBonusWithHelper = 0

  if (hasOpsDetails) {
    dailyDetailsArr.forEach(d => {
      if (d.hasHelper) {
        acceptedWithHelper += d.opsAccepted || 0
        acceptedLateWithHelper += d.opsAcceptedLate || 0
        acceptBonusWithHelper += d.acceptBonus || 0

        issuedWithHelper += d.opsIssued || 0
        issuedBonusWithHelper += d.issuedBonus || 0

        returnedWithHelper += d.opsReturned || 0
        returnBonusWithHelper += d.returnBonus || 0
      } else {
        acceptedNoHelper += d.opsAccepted || 0
        acceptedLateNoHelper += d.opsAcceptedLate || 0
        acceptBonusNoHelper += d.acceptBonus || 0

        issuedNoHelper += d.opsIssued || 0
        issuedBonusNoHelper += d.issuedBonus || 0

        returnedNoHelper += d.opsReturned || 0
        returnBonusNoHelper += d.returnBonus || 0
      }
    })
  }

  const hourlyRate = autoSalary.isHelper
    ? (autoSalary.config?.helperHourlyRate || 100)
    : ((autoSalary.config?.dailyRate || 1700) / 12)

  return (
    <div className="auto-salary-breakdown animate-fade-in" style={{ marginTop: '8px' }}>
      <div className="auto-salary-row">
        <span className="auto-salary-label">Рабочих дней</span>
        <span className="auto-salary-value">
          {autoSalary.daysWorked}
          {partialShifts > 0 ? ` (полных смен: ${fullShifts}, неполных: ${partialShifts})` : ''}
        </span>
      </div>
      <div className="auto-salary-row">
        <span className="auto-salary-label">Всего часов</span>
        <span className="auto-salary-value">{autoSalary.totalHours}ч</span>
      </div>
      <div className="auto-salary-row">
        <span className="auto-salary-label">
          {autoSalary.isHelper 
            ? `Почасовая оплата (${hourlyRate} ₽/ч × ${autoSalary.totalHours}ч)` 
            : `Фикс. часть (${hourlyRate.toFixed(1).replace(/\.0$/, '')} ₽/ч × ${autoSalary.totalHours}ч)`
          }
        </span>
        <span className="auto-salary-value">{formatMoney(autoSalary.basePay)}</span>
      </div>
      {!autoSalary.isHelper && (
        <>
          {/* Приёмка */}
          {hasOpsDetails ? (
            <>
              {acceptedNoHelper > 0 && (
                <div className="auto-salary-row">
                  <span className="auto-salary-label">
                    Приёмка ({autoSalary.config.bonusPerAccepted}₽ × {acceptedNoHelper} шт. вовремя)
                    {acceptedLateNoHelper > 0 && <span style={{display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px', fontWeight: 'normal'}}>+ {acceptedLateNoHelper} шт. с опозданием (без бонуса)</span>}
                  </span>
                  <span className="auto-salary-value" style={{ color: 'var(--success)' }}>+{formatMoney(acceptBonusNoHelper)}</span>
                </div>
              )}
              {acceptedWithHelper > 0 && (
                <div className="auto-salary-row" style={{ borderLeft: '3px solid var(--info-border)', paddingLeft: '8px' }}>
                  <span className="auto-salary-label" style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: 600 }}>Приёмка с помощником ({autoSalary.config.helperBonusPerAccepted ?? autoSalary.config.bonusPerAccepted}₽ × {acceptedWithHelper} шт. вовремя)</span>
                    {acceptedLateWithHelper > 0 && <span style={{fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px', fontWeight: 'normal'}}>+ {acceptedLateWithHelper} шт. с опозданием (без бонуса)</span>}
                  </span>
                  <span className="auto-salary-value" style={{ color: 'var(--success)' }}>+{formatMoney(acceptBonusWithHelper)}</span>
                </div>
              )}
            </>
          ) : (
            (autoSalary.totalAccepted > 0 || autoSalary.acceptBonus > 0) && (
              <div className="auto-salary-row">
                <span className="auto-salary-label">
                  Приёмка ({autoSalary.config.bonusPerAccepted}₽ × {autoSalary.totalAccepted} шт. вовремя)
                  {autoSalary.totalAcceptedLate > 0 && <span style={{display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px', fontWeight: 'normal'}}>+ {autoSalary.totalAcceptedLate} шт. с опозданием (без бонуса)</span>}
                </span>
                <span className="auto-salary-value" style={{ color: 'var(--success)' }}>+{formatMoney(autoSalary.acceptBonus)}</span>
              </div>
            )
          )}

          {/* Выдача */}
          {hasOpsDetails ? (
            <>
              {issuedNoHelper > 0 && (
                <div className="auto-salary-row">
                  <span className="auto-salary-label">Выдача ({autoSalary.config.bonusPerIssued}₽ × {issuedNoHelper} шт.)</span>
                  <span className="auto-salary-value" style={{ color: 'var(--success)' }}>+{formatMoney(issuedBonusNoHelper)}</span>
                </div>
              )}
              {issuedWithHelper > 0 && (
                <div className="auto-salary-row" style={{ borderLeft: '3px solid var(--info-border)', paddingLeft: '8px' }}>
                  <span className="auto-salary-label" style={{ fontWeight: 600 }}>Выдача с помощником ({autoSalary.config.helperBonusPerIssued ?? autoSalary.config.bonusPerIssued}₽ × {issuedWithHelper} шт.)</span>
                  <span className="auto-salary-value" style={{ color: 'var(--success)' }}>+{formatMoney(issuedBonusWithHelper)}</span>
                </div>
              )}
            </>
          ) : (
            (autoSalary.totalIssued > 0 || autoSalary.issuedBonus > 0) && (
              <div className="auto-salary-row">
                <span className="auto-salary-label">Выдача ({autoSalary.config.bonusPerIssued}₽ × {autoSalary.totalIssued} шт.)</span>
                <span className="auto-salary-value" style={{ color: 'var(--success)' }}>+{formatMoney(autoSalary.issuedBonus)}</span>
              </div>
            )
          )}

          {/* Возвраты */}
          {hasOpsDetails ? (
            <>
              {returnedNoHelper > 0 && (
                <div className="auto-salary-row">
                  <span className="auto-salary-label">Возвраты ({autoSalary.config.bonusPerReturned || 0}₽ × {returnedNoHelper} шт.)</span>
                  <span className="auto-salary-value" style={{ color: 'var(--success)' }}>+{formatMoney(returnBonusNoHelper)}</span>
                </div>
              )}
              {returnedWithHelper > 0 && (
                <div className="auto-salary-row" style={{ borderLeft: '3px solid var(--info-border)', paddingLeft: '8px' }}>
                  <span className="auto-salary-label" style={{ fontWeight: 600 }}>Возвраты с помощником ({autoSalary.config.helperBonusPerReturned || autoSalary.config.bonusPerReturned || 0}₽ × {returnedWithHelper} шт.)</span>
                  <span className="auto-salary-value" style={{ color: 'var(--success)' }}>+{formatMoney(returnBonusWithHelper)}</span>
                </div>
              )}
            </>
          ) : (
            (autoSalary.returnBonus > 0 || autoSalary.totalReturned > 0) && (
              <div className="auto-salary-row">
                <span className="auto-salary-label">Возвраты ({autoSalary.config.bonusPerReturned || 0}₽ × {autoSalary.totalReturned || 0} шт.)</span>
                <span className="auto-salary-value" style={{ color: 'var(--success)' }}>+{formatMoney(autoSalary.returnBonus || 0)}</span>
              </div>
            )
          )}
        </>
      )}

      {autoSalary.customWorkDetails && autoSalary.customWorkDetails.length > 0 && (
        <>
          {autoSalary.customWorkDetails.map((detail, idx) => (
            <div key={idx} className="auto-salary-row" style={{ paddingLeft: '28px' }}>
              <span className="auto-salary-label" style={{ fontSize: '13px' }}>
                {detail.name} ({detail.type === 'percent' ? 'Процент от' : 'Кол-во'}: {detail.value})
              </span>
              <span className="auto-salary-value" style={{ color: 'var(--success)', fontSize: '13px' }}>
                +{formatMoney(detail.amount)}
              </span>
            </div>
          ))}
        </>
      )}

      <div className="auto-salary-divider"></div>
      <div className="auto-salary-row auto-salary-subtotal">
        <span className="auto-salary-label">Начислено</span>
        <span className="auto-salary-value">{formatMoney(autoSalary.subtotal || (autoSalary.basePay + autoSalary.acceptBonus + autoSalary.issuedBonus + (autoSalary.returnBonus || 0)))}</span>
      </div>

      {autoSalary.totalBonuses > 0 && (
        <div className="auto-salary-row auto-salary-row-clickable" onClick={async (e) => {
          e.stopPropagation()
          const empId = filterEmployee || profile?.id
          const start = filterPeriodStart || firstDayOfMonth()
          const end = filterPeriodEnd || lastDayOfMonth()
          const data = await api.getPayments({ type: 'bonus', employeeId: empId, periodStart: start, periodEnd: end })
          const items = (data || []).filter(p => p.status !== 'rejected').map(p => ({
            icon: '', name: p.comment || 'Премия', date: formatDate(p.period_start),
            comment: p.created_by_name ? `от ${p.created_by_name}` : '', amount: parseFloat(p.amount), isNegative: false,
            rawRecord: { ...p, _source: 'payment' }
          }))
          if (typeof setSalaryDetailModal === 'function') {
            setSalaryDetailModal({ title: 'Детализация премий', items })
          }
        }}>
          <span className="auto-salary-label">Премии <span className="auto-salary-detail-hint">▸ подробнее</span></span>
          <span className="auto-salary-value" style={{ color: 'var(--success)' }}>+{formatMoney(autoSalary.totalBonuses)}</span>
        </div>
      )}
      {autoSalary.totalFines > 0 && (
        <div className="auto-salary-row auto-salary-row-clickable" onClick={async (e) => {
          e.stopPropagation()
          const empId = filterEmployee || profile?.id
          const start = filterPeriodStart || firstDayOfMonth()
          const end = filterPeriodEnd || lastDayOfMonth()
          const data = await api.getPayments({ type: 'fine', employeeId: empId, periodStart: start, periodEnd: end })
          const items = (data || []).filter(p => p.status !== 'rejected').map(p => ({
            icon: '', name: p.comment || p.reason_code || 'Штраф', date: formatDate(p.period_start),
            comment: p.created_by_name ? `от ${p.created_by_name}` : '', amount: parseFloat(p.amount), isNegative: true,
            rawRecord: { ...p, _source: 'payment' }
          }))
          if (typeof setSalaryDetailModal === 'function') {
            setSalaryDetailModal({ title: 'Детализация штрафов', items })
          }
        }}>
          <span className="auto-salary-label">Штрафы (внутренние) <span className="auto-salary-detail-hint">▸ подробнее</span></span>
          <span className="auto-salary-value" style={{ color: 'var(--danger)' }}>−{formatMoney(autoSalary.totalFines)}</span>
        </div>
      )}
      {autoSalary.totalWbDeductions > 0 && (
        <div className="auto-salary-row auto-salary-row-clickable" onClick={async (e) => {
          e.stopPropagation()
          const empId = filterEmployee || profile?.id
          const start = filterPeriodStart || firstDayOfMonth()
          const end = filterPeriodEnd || lastDayOfMonth()
          const periodStart = start ? new Date(start).toISOString() : undefined
          const periodEnd = end ? new Date(end + 'T23:59:59').toISOString() : undefined
          const data = await api.getDeductions(periodStart, periodEnd)
          const empData = (data || []).filter(d => d.employee_id === empId && d.status !== 'rejected')
          const typeLabels = { 'брак': 'Брак', 'подмена': 'Подмена', 'зависшие': 'Зависшие' }
          const items = empData.map(d => ({
            icon: typeLabels[d.type]?.split(' ')[0] || '',
            name: typeLabels[d.type]?.split(' ').slice(1).join(' ') || d.type || 'Удержание',
            date: new Date(d.date).toLocaleDateString('ru'),
            comment: d.comment || '', amount: parseFloat(d.amount), isNegative: true,
            rawRecord: { ...d, _source: 'deduction' }
          }))
          if (typeof setSalaryDetailModal === 'function') {
            setSalaryDetailModal({ title: 'Детализация удержаний WB', items })
          }
        }}>
          <span className="auto-salary-label">Удержания WB (брак, подмены) <span className="auto-salary-detail-hint">▸ подробнее</span></span>
          <span className="auto-salary-value" style={{ color: 'var(--danger)' }}>−{formatMoney(autoSalary.totalWbDeductions)}</span>
        </div>
      )}
      {autoSalary.helperDeduction > 0 && (
        <div className="auto-salary-row">
          <span className="auto-salary-label">Удержание за помощника ({autoSalary.config.helperDeductionPercent || 50}%)</span>
          <span className="auto-salary-value" style={{ color: 'var(--danger)' }}>−{formatMoney(autoSalary.helperDeduction)}</span>
        </div>
      )}

      <div className="auto-salary-divider"></div>

      {!autoSalary.isAnalytics && !autoSalary.isAnalyticsRange && autoSalary.actualSplitEnabled ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
            <span>Раздельный расчет ЗП:</span>
            <span style={{ color: 'var(--primary)' }}>Включен</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Оклад (ставка):</span>
            <span>{formatMoney(autoSalary.actualRateEarned ?? 0)}</span>
          </div>
          {(autoSalary.actualRatePaid ?? 0) > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--success)', fontSize: '11px' }}>
              <span>└ Выплачено:</span>
              <span>−{formatMoney(autoSalary.actualRatePaid)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed var(--border-color)', paddingBottom: '4px' }}>
            <span>└ Долг по окладной части:</span>
            <span style={{ fontWeight: 600, color: (autoSalary.actualRateDebt ?? 0) > 0 ? 'var(--danger)' : 'var(--success)' }}>
              {(autoSalary.actualRateDebt ?? 0) > 0 ? formatMoney(autoSalary.actualRateDebt) : 'Оплачено'}
            </span>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Бонусы и премии:</span>
            <span>{formatMoney(autoSalary.actualBonusesEarned ?? 0)}</span>
          </div>
          {(autoSalary.actualBonusesPaid ?? 0) > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--success)', fontSize: '11px' }}>
              <span>└ Выплачено:</span>
              <span>−{formatMoney(autoSalary.actualBonusesPaid)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>└ Долг по бонусам:</span>
            <span style={{ fontWeight: 600, color: (autoSalary.actualBonusesDebt ?? 0) > 0 ? 'var(--danger)' : 'var(--success)' }}>
              {(autoSalary.actualBonusesDebt ?? 0) > 0 ? formatMoney(autoSalary.actualBonusesDebt) : 'Оплачено'}
            </span>
          </div>
          {!autoSalary.actualBonusesPayable && (autoSalary.actualBonusesDebt ?? 0) > 0 && (
            <div style={{ fontSize: '11px', color: 'var(--info)', background: 'color-mix(in srgb, var(--info) 10%, transparent)', padding: '8px', borderRadius: '6px', marginTop: '4px', border: '1px solid color-mix(in srgb, var(--info) 20%, transparent)' }}>
              Бонусы будут доступны к выплате <strong>{formatDate(autoSalary.actualBonusPayoutDateStr)}</strong>
            </div>
          )}
        </div>
      ) : null}

      <div className="auto-salary-row auto-salary-total">
        <span className="auto-salary-label">
          {autoSalary.isAnalytics || autoSalary.isAnalyticsRange ? 'ИТОГО НАЧИСЛЕНО' : 'НАЧИСЛЕНО ЗА ПЕРИОД'}
        </span>
        <span className="auto-salary-value">{formatMoney(autoSalary.total)}</span>
      </div>

      {!autoSalary.isAnalytics && !autoSalary.isAnalyticsRange && autoSalary.debtBefore > 0 && (
        <div className="auto-salary-row" style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          <span className="auto-salary-label">Долг за предыдущие периоды</span>
          <span className="auto-salary-value" style={{ fontWeight: 600 }}>{formatMoney(autoSalary.debtBefore)}</span>
        </div>
      )}

      {!autoSalary.isAnalytics && !autoSalary.isAnalyticsRange && autoSalary.actualDebt !== undefined && autoSalary.actualDebt !== null && (
        <>
          {(() => {
            const paidTotal = (autoSalary.total + (autoSalary.debtBefore || 0)) - autoSalary.actualDebt
            const physicalPaid = autoSalary.physicalPaidInPeriod || 0
            const coveredByOverpayment = Math.max(0, paidTotal - physicalPaid)

            return (
              <>
                {physicalPaid > 0 && (
                  <div className="auto-salary-row" style={{ fontSize: '13px', color: 'var(--success)' }}>
                    <span className="auto-salary-label">ВЫПЛАТЫ ПО СВЯЗАННЫМ ПЕРИОДАМ</span>
                    <span className="auto-salary-value">−{formatMoney(physicalPaid)}</span>
                  </div>
                )}
                {coveredByOverpayment > 0 && (
                  <div className="auto-salary-row" style={{ fontSize: '13px', color: 'var(--success)', fontStyle: 'italic' }}>
                    <span className="auto-salary-label">Покрыто переплатой прошлых периодов</span>
                    <span className="auto-salary-value">−{formatMoney(coveredByOverpayment)}</span>
                  </div>
                )}
              </>
            )
          })()}
          <div className="auto-salary-row" style={{ fontSize: '15px', fontWeight: 'bold', marginTop: '4px' }}>
            <span className="auto-salary-label">ДОЛГ К ВЫПЛАТЕ</span>
            <span className="auto-salary-value" style={{ color: autoSalary.actualDebt > 0 ? 'var(--danger)' : 'var(--success)' }}>
              {autoSalary.actualDebt > 0 ? formatMoney(autoSalary.actualDebt) : 'Есть связанные выплаты'}
            </span>
          </div>
        </>
      )}

      {!autoSalary.isAnalytics && !autoSalary.isAnalyticsRange && isOwner && (autoSalary.actualDebt !== undefined ? autoSalary.actualDebt > 0 : autoSalary.total > 0) && onPaySalary && (
        <button
          className="btn btn-primary"
          disabled={isPaying}
          style={{ width: '100%', marginTop: '16px', fontSize: '14px', padding: '12px', borderRadius: '8px', display: 'flex', justifyContent: 'center', gap: '8px', alignItems: 'center' }}
          onClick={async (e) => {
            e.stopPropagation()
            setIsPaying(true)
            await onPaySalary(autoSalary.actualDebt !== undefined ? autoSalary.actualDebt : autoSalary.total)
            setIsPaying(false)
          }}
        >
          {isPaying ? 'Проведение выплаты...' : 'Выплатить ЗП'}
        </button>
      )}
      
      {autoSalary.dailyDetails && Object.keys(autoSalary.dailyDetails).length > 0 && (
        <button 
          className="btn btn-outline" 
          style={{ width: '100%', marginTop: '16px', fontSize: '13px', padding: '8px', border: '1px solid var(--surface-border)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
          onClick={(e) => {
            e.stopPropagation();
            const items = Object.values(autoSalary.dailyDetails)
              .sort((a, b) => new Date(b.date) - new Date(a.date)) // descending date
              .map(day => {
                const commentParts = []
                if (day.abandonedShift) commentParts.push('⚠️ Смена не закрыта (0ч)')
                if (day.basePay > 0) commentParts.push(`База: ${formatMoney(day.basePay)}`)
                const bonusTotal = (day.acceptBonus || 0) + (day.issuedBonus || 0) + (day.returnBonus || 0) + (day.customWorkBonus || 0) + (day.bonuses || 0)
                if (bonusTotal > 0) commentParts.push(`Бонусы: +${formatMoney(bonusTotal)}`)
                const deductionTotal = (day.fines || 0) + (day.helperDeduction || 0) + (day.wbDeductions || 0)
                if (deductionTotal > 0) commentParts.push(`Удержания: -${formatMoney(deductionTotal)}`)
                
                const dateObj = new Date(day.date)
                const dateStr = dateObj.toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'long' })
                
                return {
                  icon: day.abandonedShift ? '⚠️' : '',
                  name: dateStr,
                  comment: commentParts.join(' • '),
                  amount: Math.abs(day.total),
                  isNegative: day.total < 0
                }
              })
            if (typeof setSalaryDetailModal === 'function') {
              setSalaryDetailModal({ title: 'Детализация по дням', items })
            }
          }}
        >
          Сводка по дням
        </button>
      )}
    </div>
  )
}
